import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { SectionPanel, ClinicalEmptyState, tokens } from "../../shared/clinical-design";
import * as svc from "../services/chargeCaptureService";

/**
 * Observations — hilo tipo chat entre el coder y el médico sobre el charge.
 * Reemplaza el ida y vuelta por teléfono/email para acordar ajustes de
 * documentación o del charge slip antes del push a AdvancedMD.
 *
 * PROTOTIPO: la respuesta del médico se simula localmente. En producción los
 * mensajes se persisten vía POST /api/charges/{id}/observations y la
 * respuesta llega del provider real (portal/notificación).
 */
export default function ObservationsThread({ charge, onChargeUpdated }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [providerTyping, setProviderTyping] = useState(false);
  const bottomRef = useRef(null);

  const observations = charge.observations || [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [observations.length, providerTyping]);

  const handleSend = async () => {
    const message = text.trim();
    if (!message || sending) return;
    setSending(true);
    try {
      const updated = await svc.addObservation(charge.chargeId, message);
      onChargeUpdated(updated);
      setText("");
      // Respuesta simulada del médico (solo prototipo)
      setProviderTyping(true);
      svc
        .simulateProviderReply(charge.chargeId)
        .then((withReply) => onChargeUpdated(withReply))
        .finally(() => setProviderTyping(false));
    } finally {
      setSending(false);
    }
  };

  return (
    <SectionPanel
      title={`Observations — provider dialogue (${observations.length})`}
      contentSx={{ p: 0 }}
    >
      <Box sx={{ maxHeight: 280, overflowY: "auto", px: 2, py: 1.5 }}>
        {observations.length === 0 && (
          <ClinicalEmptyState
            message="No observations yet. Ask the provider about documentation or charge adjustments — the conversation stays attached to this charge."
            sx={{ py: 2 }}
          />
        )}
        {observations.map((m) => {
          const isCoder = m.author === "coder";
          return (
            <Stack
              key={m.id}
              alignItems={isCoder ? "flex-end" : "flex-start"}
              sx={{ mb: 1 }}
            >
              <Box
                sx={{
                  maxWidth: "85%",
                  px: 1.5,
                  py: 0.8,
                  borderRadius: "8px",
                  borderTopRightRadius: isCoder ? "2px" : "8px",
                  borderTopLeftRadius: isCoder ? "8px" : "2px",
                  bgcolor: isCoder ? tokens.primary50 : tokens.surface2,
                  border: `1px solid ${isCoder ? tokens.primary : tokens.line}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: isCoder ? tokens.primary700 : tokens.ink2,
                    }}
                  >
                    {m.authorName}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: tokens.ink3 }}>
                    {new Date(m.at).toLocaleString()}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 13, color: tokens.ink, mt: 0.3 }}>
                  {m.text}
                </Typography>
              </Box>
            </Stack>
          );
        })}
        {providerTyping && (
          <Typography sx={{ fontSize: 12, color: tokens.ink3, fontStyle: "italic" }}>
            {charge.providerName} is typing…
          </Typography>
        )}
        <div ref={bottomRef} />
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, py: 1.2, borderTop: `1px solid ${tokens.line}` }}
      >
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={3}
          placeholder="Write an observation for the provider… (e.g. request an addendum, propose a more specific code)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
        />
        <Button
          variant="contained"
          disabled={!text.trim() || sending}
          onClick={handleSend}
          endIcon={<SendIcon sx={{ fontSize: 15 }} />}
          sx={{
            bgcolor: tokens.primary,
            "&:hover": { bgcolor: tokens.primary700 },
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          Send
        </Button>
      </Stack>
    </SectionPanel>
  );
}
